// Session PJ2: the device half of a project survey visit. Ties the IndexedDB survey stores (db.ts) to the sheet.
//
// EVERY CALL IS BEST EFFORT AND NONE REJECTS, like the job sheet's draftStore.ts: a storage failure must never break the
// sheet in front of an engineer in a plant room. A failed write is retried by the next change; a failed read is an empty
// draft. The rows are the engineer's own unfinished work and survive a sign-out (db.ts gives them no clear()).
import { surveyDraftStore } from "./db";
import type { SurveyDraftRow, SurveyVisitDraftRow } from "../projectSurveyDraft";
import { storageKeySuffix } from "../phonePreview";

export async function loadSurveyVisit(jobId: string): Promise<{ visit: SurveyVisitDraftRow | undefined; surveys: SurveyDraftRow[] }> {
  let visit: SurveyVisitDraftRow | undefined;
  let surveys: SurveyDraftRow[] = [];
  try {
    visit = await surveyDraftStore.getVisit(jobId);
  } catch {
    visit = undefined;
  }
  try {
    surveys = await surveyDraftStore.getSurveysForJob(jobId);
  } catch {
    surveys = [];
  }
  return { visit, surveys };
}

export async function saveSurveyVisit(row: SurveyVisitDraftRow): Promise<void> {
  try {
    await surveyDraftStore.putVisit(row);
  } catch {
    /* best effort: the next change writes it again */
  }
}

export async function saveSurveyDraft(row: SurveyDraftRow): Promise<void> {
  try {
    await surveyDraftStore.putSurvey(row);
  } catch {
    /* best effort */
  }
}

/** After the visit's completion is queued (it carries everything), the drafts have done their job. */
export async function clearSurveyVisit(jobId: string): Promise<void> {
  try {
    const rows = await surveyDraftStore.getSurveysForJob(jobId);
    for (const r of rows) await surveyDraftStore.deleteSurvey(r.surveyId);
    await surveyDraftStore.deleteVisit(jobId);
  } catch {
    /* best effort: a leftover row is harmless (the next open merges it, newer wins) */
  }
}

// ---- Messages about survey photos that could not be sent ----------------------------------------------------------------
// A survey photo dropped because the office removed its survey is gone for good, so the engineer is told, once, in words
// that name the lift. Kept on the device (localStorage, the Phone view's own key inside the frame) until dismissed.
const NOTICES_KEY = () => `ele.engineer.surveyPhotoNotices${storageKeySuffix()}`;
export const SURVEY_NOTICES_EVENT = "ele:survey-photo-notices";

export function readSurveyPhotoNotices(): string[] {
  try {
    const raw = window.localStorage.getItem(NOTICES_KEY());
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function addSurveyPhotoNotice(notice: string): void {
  try {
    const list = readSurveyPhotoNotices();
    if (!list.includes(notice)) list.push(notice);
    window.localStorage.setItem(NOTICES_KEY(), JSON.stringify(list.slice(-20)));
    window.dispatchEvent(new Event(SURVEY_NOTICES_EVENT));
  } catch {
    /* best effort */
  }
}

/** A survey entry the server did not apply when the visit's sheet arrived (the reply's projectSurvey.skipped). */
export interface SkippedSurveyEntry {
  surveyId: string;
  label: string | null;
  reason: string;
}

/** What the engineer reads about one such entry: plain words naming the lift. The office holds the same list on the visit's audit row. */
export function surveySkippedNotice(s: SkippedSurveyEntry): string {
  const name = s.label && s.label.trim() ? s.label.trim() : "a survey";
  const why =
    s.reason === "deleted"
      ? "the office removed that survey"
      : s.reason === "superseded"
        ? "the office has started a new survey of it"
        : "it is no longer on this visit";
  return `Your changes to ${name} were not saved: ${why}. The rest of the visit was sent. Ring the office if they are needed.`;
}

export function dismissSurveyPhotoNotice(notice: string): void {
  try {
    window.localStorage.setItem(NOTICES_KEY(), JSON.stringify(readSurveyPhotoNotices().filter((n) => n !== notice)));
    window.dispatchEvent(new Event(SURVEY_NOTICES_EVENT));
  } catch {
    /* best effort */
  }
}
